import { useClipboardPaste, useToastService } from "@@common";
import { ReactFlowInstance, useReactFlow } from "@xyflow/react";

import { useFlowMetadata } from "../Hooks/Metadata";
import { FlowEdge, FlowNode } from "../Models/GraphModel";

type PasteHandlerProps = {
	instance: ReactFlowInstance<FlowNode, FlowEdge> | undefined;
	setNodes: React.Dispatch<React.SetStateAction<FlowNode[]>>;
	setEdges: React.Dispatch<React.SetStateAction<FlowEdge[]>>;
	toastService: ReturnType<typeof useToastService>;
	workspaceContainerRef: React.RefObject<HTMLDivElement>;
};

export default function PasteHandler({ instance, setNodes, setEdges, toastService, workspaceContainerRef }: PasteHandlerProps) {
	const { flowMetadata, clipboardService } = useFlowMetadata();
	const { getNodes } = useReactFlow<FlowNode, FlowEdge>();

	useClipboardPaste(
		(text, event) => {
			if (!flowMetadata.activities || flowMetadata.activities.length === 0) return;

			// Compute the viewport centre as a fallback paste position
			const rect = workspaceContainerRef.current?.getBoundingClientRect();
			const fallback = rect
				? (instance?.screenToFlowPosition({ x: (rect.left + rect.right) / 2, y: (rect.top + rect.bottom) / 2 }) ?? { x: 0, y: 0 })
				: { x: 0, y: 0 };

			const result = clipboardService.parseAndBuildPasteResult(
				text,
				getNodes().filter((n) => n.selected),
				fallback
			);
			if (!result) return;

			event.preventDefault();
			setNodes((prev) => [...prev, ...result.nodes]);
			if (result.edges.length > 0) setEdges((prev) => [...prev, ...result.edges]);
			toastService.success("Activity pasted from clipboard");
		},
		true,
		true
	);

	return null;
}
